import { ISuccessApiResponse } from '@wyz/types';
import { FullPageSpinner } from '@wyz/ui';
import { callApi, invariant, useOnSuccessQuery, useQuery } from '@wyz/utils';
import * as React from 'react';
import { useAuth } from 'react-oidc-context';
import { CommonQueryKeys } from '~/common/api';
import {
  CookieConsentModal,
  TermsOfUseConsentModal,
} from '~/common/components';
import { useConfig, useLocale } from '~/common/context';
import { GROUPS, WYZ_GROUPS, applyTheme, initUsetiful } from '~/common/helpers';
import { IAuthContext, IApplicationContext, IUserAuth } from '~/common/types';
// eslint-disable-next-line @wyz/module/no-relative-import
import { ApplicationContext } from './contexts';

export function ApplicationProvider(props: React.PropsWithChildren) {
  const config = useConfig();
  const auth = useAuth();
  const locale = useLocale();
  invariant(auth.user?.access_token);
  const call = callApi({});

  const applicationContextQuery = useQuery({
    queryKey: CommonQueryKeys.appContext(auth.user?.profile.sub),
    queryFn: () =>
      call<ISuccessApiResponse<IApplicationContext>>(
        `${config.apiUrl}/tenants/api/wtb/tenants/${config.tenantCode}/application-context/me`,
      ),
  });

  useOnSuccessQuery(applicationContextQuery, (applicationContext) => {
    const rootElement = document.querySelector(':root') as HTMLElement;
    const user = auth.user as unknown as IUserAuth;
    if (rootElement && applicationContext.data.userTheme) {
      applyTheme(applicationContext.data.userTheme);
    }
    if (user?.profile.sub && config.usetifulEnabled) {
      const userGroups = JSON.stringify(user?.profile.groups ?? []);
      initUsetiful(
        user?.profile.sub,
        userGroups,
        locale.language,
        applicationContext.data.features.enabledRedistribution,
      );
    }
  });

  const context: IAuthContext = React.useMemo(() => {
    const user = auth.user as unknown as IUserAuth;
    return {
      auth: {
        ...auth,
        userId: user?.profile.sub,
        isWyzUser:
          user.profile?.groups?.some((group) => WYZ_GROUPS.includes(group)) ||
          false,
        roles: user?.profile.realm_access.roles ?? [],
        isPlatformAdmin:
          user?.profile.groups?.includes(GROUPS.GROUP_PLATFORM_ADMIN) ?? false,
      },
      application: applicationContextQuery.data?.data as IApplicationContext,
    };
  }, [auth, applicationContextQuery.data?.data]);

  if (!applicationContextQuery.isSettled) return <FullPageSpinner />;

  if (!applicationContextQuery.isSuccess) {
    // TODO: clean way to display error
    // eslint-disable-next-line @wyz/i18n/no-plain-text
    return <div>Oops... {JSON.stringify(applicationContextQuery.error)}</div>;
  }

  // terms of use
  if (!applicationContextQuery.data.data.termsOfUseAccepted) {
    return <TermsOfUseConsentModal />;
  }
  return (
    <ApplicationContext.Provider value={context}>
      {/* cookie */}
      {!applicationContextQuery.data.data.cookieConsentAccepted && (
        <CookieConsentModal />
      )}
      {props.children}
    </ApplicationContext.Provider>
  );
}

export const useApplicationContext = () => {
  const context = React.useContext(ApplicationContext);
  if (context === undefined) {
    throw new Error('useApplicationContext must be used within a AuthContext');
  }
  return context;
};
